/**
 * account.js — Account Optional Feature (Fogarty / Wine Engine — Shopify;
 * Lightning Rock Winery — Commerce7).
 *
 * PURPOSE:
 *   Greets the shopper on first panel open. If a logged-in customerId is known
 *   (state.user.customerId, sourced from the host's data-customer-id embed
 *   attribute — see core/env.js getCustomerId), it shows a personalized
 *   "Welcome back" greeting plus account prompts (View cart / My orders /
 *   Membership). Otherwise it shows a logged-out message plus Login / Register
 *   prompts.
 *
 * ARCHITECTURE:
 *   Shopify tenants: FE → BE → Shopify via core/api.js (getLoginGreeting /
 *   getCustomer); Login/Register open the storefront URLs from tenant config.
 *   Commerce7 tenants (tenant.features.commerce7): Register still opens the
 *   storefront URL (old showDefaultUserMessage always linked out for both),
 *   but Login renders the in-chat email/password form (kind "login-form",
 *   old showLoginForm/onLogin) — the widget can't reload the host to /profile/
 *   like the old FE did, so the token lives in localStorage
 *   (ws_c7token_<tenantId>) + state.user, same convention as account_c7.js
 *   (Valle della Pace's counterpart feature — kept separate because that
 *   variant's welcome/greeting flow differs enough to need its own file; the
 *   C7 login primitives here are a small, self-contained duplicate, not a
 *   dependency on it).
 *
 * ENABLED BY:
 *   tenant.features.shopify = true AND tenant.ui_features.show_user_account = true
 *   (or tenant.features.commerce7 = true for the Commerce7 branch above).
 *   Wired from variants/fogartywinery|wineengine|lightningrockwinery/index.js register().
 *
 * EVENTS:
 *   in:  ws-welcome-seeded  (greet after welcome on open + refresh),
 *        ws-prompt-select {value}  (handle account prompt chips, incl.
 *          __login__/__register__/__logout__),
 *        ws-login-submit {email, password}  (Commerce7 login-form submit),
 *        ws-dev-customer-login {customerId}  (dev chat shortcut from chat.js)
 *   out: ws-cart-open  (when "View cart" is chosen),
 *        ws-login-success / ws-login-error {message, field}  (Commerce7 login-form)
 *
 * REFERENCE (old code):
 *   thomasfogarty/script.js getCustomerDetails / login message (2619-2710);
 *   Lightning Rock RedConcierge/static/javascript/main.js showDefaultUserMessage
 *   (3913), showLoginForm (2060), onLogin (4065).
 *
 * EXPORTS:
 *   init() — subscribes to open + prompt events; idempotent.
 */

import * as api from "../../core/api.js";
import { on, emit, getState, setState, addMessage, removeMessage } from "../../core/state.js";
import { getTheme, getFeature, getTenantId } from "../../core/configAccessors.js";
import { error } from "../../core/env.js";
import { ensureSession } from "../base/chat-handler.js";
import { endSession } from "../../core/session.js";

// ─── Commerce7 login (Lightning Rock) — self-contained, mirrors
// account_c7.js's token/customer handling without depending on it. ─────────

const c7TokenKey = () => `ws_c7token_${getTenantId() || "default"}`;

function loadC7Token() {
  try {
    return localStorage.getItem(c7TokenKey()) || null;
  } catch {
    return null;
  }
}

function persistC7Token(token) {
  setState({ user: { ...getState().user, customerToken: token } });
  try {
    if (token) localStorage.setItem(c7TokenKey(), token);
    else localStorage.removeItem(c7TokenKey());
  } catch {
    /* storage blocked — token stays in memory for this page only */
  }
}

function getC7Token() {
  return getState().user?.customerToken || loadC7Token();
}

// Old loginPrompts (script.js 189) equivalent — chat queries after a
// Commerce7 login.
const C7_LOGIN_PROMPTS = [
  { label: "View cart", value: "Show me my cart", icon: "bolt" },
  { label: "View order history", value: "Show me my past orders", icon: "bolt" },
  { label: "View membership details", value: "Show me my membership details", icon: "bolt" }
];

async function resolveC7Customer(token) {
  try {
    const profile = await api.c7GetCustomerSelf(token);
    setState({
      user: {
        ...getState().user,
        customerToken: token,
        customerId: profile?.id || profile?.customerId || "",
        profile: profile || null,
        isAuthenticated: true,
      },
    });
    return profile;
  } catch (e) {
    error("[account] c7 customer self failed", e);
    return null;
  }
}


function greetC7(profile, { isFirstResponse = true } = {}) {
  // Remove any previous greeting (not just forget it) — otherwise a second
  // call (e.g. a duplicate ws-login-submit) leaves the earlier one orphaned
  // and permanently visible instead of replacing it.
  clearLoggedInFlow();
  const first = profile?.firstName || profile?.first_name || "";
  trackLoggedIn(showText(first ? `Welcome back, ${first}!` : "Welcome back!", { isFirstResponse }));
  trackLoggedIn(showPrompts(C7_LOGIN_PROMPTS, { isFirstResponse: false }));
}

async function greetIfLoggedInC7() {
  const token = getC7Token();
  if (!token) return false;
  let profile = getState().user?.profile;
  if (!profile) profile = await resolveC7Customer(token);

  // Restoring a still-valid session (page reload) is also a "now logged in"
  // transition for anything reactive to it — the header account button only
  // re-renders on this event, and handleC7LoginSubmit is the ONLY other place
  // that fires it, so without this the header stays stuck on its initial
  // (logged-out) render after a reload even though the chat greeting above
  // correctly shows the user as logged in.
  emit("ws-login-success");
  greetC7(profile, { isFirstResponse: false });
  return true;
}

function showLoginFormC7() {
  // Old showLoginForm: its own intro line, separate from the
  // showDefaultUserMessage line that led here. Tracked in the same
  // "logged out flow" group as greetLoggedOut's messages — skipLoginForm
  // cleared both together.
  trackLoggedOut(showText("Log in for personalized wine picks and a better experience."));
  trackLoggedOut(addMessage({ role: "bot", kind: "login-form", data: {} }));
}

// The form itself shows the loading state (submit button disabled + "Logging
// in..."), not the chat-wide typing-dots indicator — a form submission isn't
// "the bot is composing a reply". loginInFlight also guards against a
// duplicate ws-login-submit (e.g. a double click before the button disables)
// re-running this and duplicating the post-login greeting.
let loginInFlight = false;

async function handleC7LoginSubmit({ email, password } = {}) {
  if (loginInFlight) return;
  loginInFlight = true;
  try {
    const sessionId = getState().session?.id || "";
    const data = await api.c7Login({ email, password, sessionId });
    const token = data?.token;
    if (!token) {
      emit("ws-login-error", { message: "Login failed. Please try again." });
      return;
    }
    persistC7Token(token);
    const profile = await resolveC7Customer(token);
    emit("ws-login-success");
    clearLoggedOutFlow();
    greetC7(profile);
  } catch (e) {
    error("[account] c7 login failed", e);
    if (e?.status === 401) emit("ws-login-error", { message: "Invalid email or password" });
    else if (e?.status === 502) emit("ws-login-error", { message: "Server error. Please try again." });
    else emit("ws-login-error", { message: "Something went wrong. Please try again." });
  } finally {
    loginInFlight = false;
  }
}

function handleC7Logout() {
  persistC7Token(null);
  setState({
    user: {
      ...getState().user,
      customerToken: null,
      customerId: "",
      profile: null,
      isAuthenticated: false,
    },
  });
  clearLoggedInFlow();
  // Tracked as a "logged out" flow (same group greetLoggedOut/showLoginFormC7
  // use) so logging back in clears this message + its prompts together.
  trackLoggedOut(showText("You've been logged out."));
  trackLoggedOut(
    showPrompts([
      { label: "Login", value: "__login__", icon: "bolt" },
      { label: "Register", value: "__register__", icon: "bolt" },
    ]),
  );
  // Logout ends the conversation handle too — the next open/message starts a
  // brand-new session instead of resuming this now-signed-out one.
  endSession(getTenantId());
  // Lets the header account button (and anything else showing logged-in
  // state) refresh itself.
  emit("ws-logout-success");
}

function isTrue(v) {
  return v === true || v === "true";
}

function showText(text, { isFirstResponse = true } = {}) {
  addMessage({ role: "bot", kind: "text", isFirstResponse, data: { text } });
}

function showPrompts(items, { isFirstResponse = false } = {}) {
  if (items.length) {
    return addMessage({ role: "bot", kind: "prompts", isFirstResponse, data: { items } });
  }
  return null;
}

// Old skipLoginForm() parity: the "logged out" greeting + Login/Register
// chips, plus the login-form's own intro line + the form itself, are cleared
// as one group — on login success OR "Continue without login" (skipLoginForm
// was called from both in the old widget). The "logged in" greeting + its
// prompts are a separate group, cleared on logout.
let loggedOutFlowIds = [];
let loggedInFlowIds = [];

function trackLoggedOut(msg) {
  if (msg?.id) loggedOutFlowIds.push(msg.id);
}

function clearLoggedOutFlow() {
  loggedOutFlowIds.forEach((id) => removeMessage(id));
  loggedOutFlowIds = [];
}

function trackLoggedIn(msg) {
  if (msg?.id) loggedInFlowIds.push(msg.id);
}

function clearLoggedInFlow() {
  loggedInFlowIds.forEach((id) => removeMessage(id));
  loggedInFlowIds = [];
}

function openUrl(url) {
  // Same-tab navigation to match old openLink (window.location.href), not a new tab.
  if (url) window.location.href = url;
}

async function greetLoggedIn(customerId) {
  // Old Wine Engine get_login_message → { message, buttons }.
  // New: shopify/login-greeting/ only (no customer/ prefetch).
  const fallbackButtons = [
    { label: "View cart", value: "Show me my cart" },
    { label: "View order history", value: "Show me my past orders" },
    { label: "View subscription details", value: "Show me my subscription details" },
  ];

  let message = "Welcome back!";
  let buttons = fallbackButtons;
  // 3-dot typing until login-greeting returns (customerId= shortcut + refresh).
  emit("ws-chat-typing", { active: true });
  try {
    // customerId= shortcut can run before any chat turn; ensureSession so
    // login-greeting always gets a real sessionId (not sessionId=).
    const sessionId = (await ensureSession()) || getState().session?.id || "";
    const greeting = await api.getLoginGreeting(customerId, sessionId);
    if (greeting?.message) message = greeting.message;
    if (Array.isArray(greeting?.buttons) && greeting.buttons.length) {
      buttons = greeting.buttons;
    }
  } catch (e) {
    error("[account] login greeting failed", e);
  } finally {
    emit("ws-chat-typing", { active: false });
  }

  showText(message, { isFirstResponse: false });
  showPrompts(
    buttons.map((b) => ({
      label: b.label || b.value,
      value: b.value || b.label,
      icon: "bolt",
    })),
    { isFirstResponse: false },
  );
}

function greetLoggedOut() {

  trackLoggedOut(showText("Looks like you're not logged in. Want to sign in or create a new account?", { isFirstResponse: false }));
  const items = [];
  if(getFeature("commerce7")){
    items.push({ label: "Login", value: "__login__", icon: "bolt" });
    items.push({ label: "Register", value: "__register__", icon: "bolt" });
  }
  if(getFeature("shopify")){
    items.push({ label: "Login", value: "__login__", icon: "bolt" });
    items.push({ label: "Register", value: "__register__", icon: "bolt" });
  }
  trackLoggedOut(showPrompts(items, { isFirstResponse: false }));
}

async function greet() {
  // Commerce7 tenants: "logged in" is the c7 customerToken (localStorage),
  // not the Shopify data-customer-id embed attribute.
  if (getFeature("commerce7")) {
    const greeted = await greetIfLoggedInC7();
    if (!greeted) greetLoggedOut();
    return;
  }
  const customerId = getState().user.customerId;
  if (customerId) return greetLoggedIn(customerId);
  greetLoggedOut();
}

// map_shopify_customer_to_c7 → { firstName, lastName, clubs, is_club_member,
// orders_summary, ... }. Shapes are defensive: orders_summary may be an object
// or array, clubs may be strings or { name } objects.
function formatOrders(customer) {
  const o = customer?.orders_summary;
  if (!o) return "I don't see any recent orders on your account.";
  if (Array.isArray(o)) {
    return `You have ${o.length} recent order${o.length === 1 ? "" : "s"}.`;
  }
  if (typeof o === "object") {
    const count = o.count ?? o.total_orders ?? o.orders;
    const total = o.total ?? o.total_spent;
    const parts = [];
    if (count != null) parts.push(`${count} order${Number(count) === 1 ? "" : "s"}`);
    if (total != null) parts.push(`totaling ${total}`);
    return parts.length ? `You have ${parts.join(", ")}.` : "Here's your order summary.";
  }
  return String(o);
}

function formatMembership(customer) {
  if (isTrue(customer?.is_club_member)) {
    const clubs = customer?.clubs;
    const names = Array.isArray(clubs)
      ? clubs.map((c) => (typeof c === "string" ? c : c?.name)).filter(Boolean)
      : [];
    return names.length
      ? `You're a member of: ${names.join(", ")}.`
      : "You're an active wine club member.";
  }
  return "You're not currently a wine club member.";
}

async function handlePrompt({ value } = {}) {
  if (value === "__view_cart__") {
    emit("ws-cart-open");
    return;
  }
  if (value === "__login__") {
    // Old Lightning Rock (RedConcierge main.js showDefaultUserMessage): Login
    // opens the in-chat form; Register always links out — same for Shopify.
    if (getFeature("commerce7")) {
      showLoginFormC7();
      return;
    }
    openUrl(getTheme("account_login_url", null));
    return;
  }
  if (value === "__register__") {
    openUrl(getTheme("account_register_url", null));
    return;
  }
  if (value === "__logout__") {
    if (getFeature("commerce7")) handleC7Logout();
    return;
  }
  if (value === "__orders__" || value === "__membership__") {
    const customerId = getState().user.customerId;
    if (!customerId) return;
    try {
      const customer = await api.getCustomer(customerId);
      showText(value === "__orders__" ? formatOrders(customer) : formatMembership(customer));
    } catch (e) {
      error("[account] customer fetch failed", e);
      showText("Sorry, I couldn't load that right now.");
    }
  }
}

let started = false;

export async function init() {
  if (started) return;
  started = true;

  // Resolve a stored Commerce7 token as soon as the feature starts, not only
  // when the welcome flow (re)greets. MessageList._seed — the only emitter of
  // ws-welcome-seeded below — is skipped whenever the panel already has
  // persisted messages (a plain page reload mid-conversation), so on that
  // path greet() never runs and state.user.profile/customerToken would stay
  // empty until the next fresh login. Resolving here instead means the header
  // account button (which reads state.user on render + re-renders on
  // ws-login-success) always reflects "logged in" after a reload, independent
  // of whether the chat transcript needed reseeding. greetIfLoggedInC7() below
  // reuses this cached profile instead of re-fetching it.
  if (getFeature("commerce7")) {
    const token = getC7Token();
    if (token) resolveC7Customer(token).then(() => emit("ws-login-success"));
  }

  // Greet on ws-welcome-seeded (emitted by MessageList._seed right AFTER the
  // welcome + guided prompts are added — on first open, and on an explicit
  // refresh/clear, not on a plain reload that restores existing messages).
  // This is deterministic: the login / returning-customer message always lands
  // after the welcome, scrolls into view as the newest message, survives a chat
  // refresh, and avoids the old ws-toggle race (where greeting could run before
  // the welcome was seeded, or be missed entirely). Mirrors old
  // showUserMessageAndPrompts() running at the end of the welcome render.
  on("ws-welcome-seeded", () => {
    greet();
  });

  on("ws-prompt-select", handlePrompt);

  // Commerce7 in-chat login form submit (login-form component).
  on("ws-login-submit", handleC7LoginSubmit);
  // "Continue without login" — old skipLoginForm() ran on this too, not just
  // on successful login.
  on("ws-login-skip", clearLoggedOutFlow);

  // Dev chat shortcut from chat.js: type exactly "customerId=<id>" and Enter.
  // Sets state.user.customerId, calls login-greeting, shows the 3 account chips.
  on("ws-dev-customer-login", ({ customerId } = {}) => {
    const id = String(customerId || "").trim();
    if (!id) return;
    setState({ user: { ...getState().user, customerId: id } });
    greetLoggedIn(id);
  });
}
